import mapboxgl from 'mapbox-gl';
import {useTranslation} from 'next-i18next';
import {useMemo} from 'react';

import useMapLoadedEffect from '~utils/map/events/useMapLoadedEffect';
import {mapAddLayerIfNotExist, mapAddSourceIfNotExist} from '~utils/map/layer';
import {Colors} from '~constants/colors';
import {MapRef} from '~types/Map';
import {districtsCoords} from '~constants/districts';
import {MapLayer, MapSource} from '~constants/plots';

const layoutSettings: mapboxgl.SymbolLayout = {
    'text-field': ['get', 'name'],
    'text-justify': 'auto',
    'text-size': 16,
    'text-offset': [0, 1.5],
};

const paintSettings: mapboxgl.SymbolPaint = {
    'text-color': Colors.White,
    'text-halo-blur': 3,
    'text-halo-color': 'rgba(0, 0, 0, 0.5)',
    'text-halo-width': 0.3,
};

const zoomSettings = {
    minzoom: 13.9,
    maxzoom: 16.1,
};

const EDzoomSettings = {
    minzoom: 0,
    maxzoom: 14,
};

export default function useMapDistrictNamesLayer(mapRef: MapRef) {
    const {t} = useTranslation('common');

    const districts = useMemo(
        () =>
            t('districts', {returnObjects: true}) as {
                [key: string]: [number, number];
            },
        [t],
    );

    const setupMap = (map: mapboxgl.Map) => {
        mapAddSourceIfNotExist(map, MapSource.HQLabel, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        properties: {name: t('HqLabel')},
                        geometry: {
                            type: 'Point',
                            coordinates: districtsCoords['HQ'],
                        },
                    },
                ],
            },
        });

        mapAddSourceIfNotExist(map, MapSource.EverdomeCity, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        properties: {name: t('EverdomeCity')},
                        geometry: {
                            type: 'Point',
                            coordinates: districtsCoords['HQ'],
                        },
                    },
                ],
            },
        });

        mapAddSourceIfNotExist(map, MapSource.DistrictNames, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: Object.keys(districts).map((key: string) => ({
                    type: 'Feature',
                    properties: {name: t('district', {name: districts[key]})},
                    geometry: {
                        type: 'Point',
                        coordinates: districtsCoords[key],
                    },
                })),
            },
        });

        mapAddLayerIfNotExist(map, {
            id: MapLayer.DistrictNames,
            type: 'symbol',
            source: MapSource.DistrictNames,
            layout: layoutSettings,
            paint: paintSettings,
            ...zoomSettings,
        });

        mapAddLayerIfNotExist(map, {
            id: MapLayer.HQLabel,
            type: 'symbol',
            source: MapSource.HQLabel,
            layout: {
                ...layoutSettings,
                'text-max-width': 4,
            },
            paint: paintSettings,
            ...zoomSettings,
        });

        mapAddLayerIfNotExist(map, {
            id: MapLayer.EverdomeCity,
            type: 'symbol',
            source: MapSource.EverdomeCity,
            layout: {
                ...layoutSettings,
            },
            paint: paintSettings,
            ...EDzoomSettings,
        });
    };

    useMapLoadedEffect((map) => {
        setupMap(map);
    }, mapRef);
}
